import { useEffect } from "react";
import {
  getBackgroundUploadServiceWorkerMessage,
  processBackgroundUploadQueue,
} from "@/lib/background-upload-queue";
import { useBackgroundUploadQueue } from "@/hooks/use-background-upload-queue";
import { useProgressStore } from "@/lib/progress-store";

export function BackgroundUploadManager() {
  const { jobs, activeJob } = useBackgroundUploadQueue();
  const {
    isActive,
    startProgress,
    updateProgress,
    stopProgress,
  } = useProgressStore();

  useEffect(() => {
    void processBackgroundUploadQueue();

    const resumeQueue = () => {
      if (document.visibilityState === "hidden") return;
      void processBackgroundUploadQueue();
    };

    window.addEventListener("online", resumeQueue);
    window.addEventListener("focus", resumeQueue);
    document.addEventListener("visibilitychange", resumeQueue);

    const handleServiceWorkerMessage = (event: MessageEvent<{ type?: string }>) => {
      if (event.data?.type !== getBackgroundUploadServiceWorkerMessage()) return;
      void processBackgroundUploadQueue();
    };

    navigator.serviceWorker?.addEventListener("message", handleServiceWorkerMessage);
    return () => {
      window.removeEventListener("online", resumeQueue);
      window.removeEventListener("focus", resumeQueue);
      document.removeEventListener("visibilitychange", resumeQueue);
      navigator.serviceWorker?.removeEventListener("message", handleServiceWorkerMessage);
    };
  }, []);

  useEffect(() => {
    if (!jobs.length || !activeJob) {
      if (isActive) {
        stopProgress();
      }
      return;
    }

    const label =
      activeJob.status === "processing"
        ? `Uploading ${activeJob.fileName}`
        : `Queued ${jobs.length} upload${jobs.length === 1 ? "" : "s"}`;

    if (!isActive) {
      startProgress(label, "green");
    }

    updateProgress(activeJob.status === "processing" ? activeJob.progress : 5, label);
  }, [activeJob, isActive, jobs.length, startProgress, stopProgress, updateProgress]);

  return null;
}
